/**
 * Word (.docx) preview: the file is converted to simple HTML in the browser
 * (mammoth: text, headings, lists, tables, links, images — not the exact page
 * layout, headers/footers or fancy formatting) and then cleaned before it is
 * shown, because the HTML comes from a file somebody uploaded.
 *
 * The cleaning is an allow-list, not a block-list: only the tags and
 * attributes below survive, scripts/styles/event handlers/iframes are dropped,
 * a link keeps its URL only if it is http(s) or mailto, and an image only if it
 * is inline png/jpeg/gif data. Every link opens in a new tab with
 * rel="noopener noreferrer".
 */
import DOMPurify from 'dompurify'

const ALLOWED_TAGS = [
  'p',
  'br',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'strong',
  'em',
  'u',
  's',
  'sub',
  'sup',
  'ul',
  'ol',
  'li',
  'table',
  'thead',
  'tbody',
  'tfoot',
  'tr',
  'th',
  'td',
  'a',
  'img',
  'blockquote',
]

const ALLOWED_ATTR = ['href', 'src', 'alt', 'colspan', 'rowspan']

// Links: http(s) and mailto only.
const ALLOWED_URI_REGEXP = /^(?:https?:|mailto:)/i

// Images: only inline png/jpeg/gif data (what mammoth produces for pictures in
// the document). Nothing is fetched from another site, so opening a document
// can't phone home, and an inline SVG (which can carry script) is refused.
// DOMPurify lets any data: URI through on an <img>, so this is checked here.
const INLINE_IMAGE_REGEXP = /^data:image\/(?:png|jpe?g|gif);base64,/i

let purifier = null

// Its own DOMPurify instance, so the hooks below can't affect any other code in
// the app that uses the shared one (jsPDF does).
function getPurifier() {
  if (purifier) return purifier

  purifier = DOMPurify(window)
  purifier.addHook('uponSanitizeAttribute', (node, data) => {
    if (data.attrName === 'src' && !INLINE_IMAGE_REGEXP.test(data.attrValue)) {
      data.keepAttr = false
    }
  })
  purifier.addHook('afterSanitizeAttributes', (node) => {
    if (node.tagName === 'A') {
      if (node.getAttribute('href')) {
        node.setAttribute('target', '_blank')
        node.setAttribute('rel', 'noopener noreferrer')
      }
    }
  })

  return purifier
}

/** Clean converted HTML down to the allow-list above. */
export function sanitizeDocxHtml(html) {
  return getPurifier().sanitize(String(html || ''), {
    ALLOWED_TAGS,
    ALLOWED_ATTR,
    // colspan / rowspan are plain numbers, not URLs: without this they would be
    // checked against ALLOWED_URI_REGEXP and dropped, flattening merged cells.
    ADD_URI_SAFE_ATTR: ['colspan', 'rowspan'],
    ALLOWED_URI_REGEXP,
    ALLOW_DATA_ATTR: false,
    ALLOW_ARIA_ATTR: false,
  })
}

/**
 * Convert a .docx (an ArrayBuffer) to clean HTML. mammoth is loaded on first
 * use so it is not part of the main bundle. Rejects if the file can't be read
 * as a Word document.
 *
 * @param {ArrayBuffer} arrayBuffer
 * @returns {Promise<string>} '' when the document has no content
 */
export async function docxToSafeHtml(arrayBuffer) {
  const loaded = await import(/* webpackChunkName: "docx-preview" */ 'mammoth')
  const mammoth = loaded.default || loaded
  const { value } = await mammoth.convertToHtml({ arrayBuffer })

  return sanitizeDocxHtml(value).trim()
}
